/* The section emits this file once per rail, and the browser runs every one of
   those tags, so the whole file lives in a function: re-running it must not
   redeclare anything. DRAG_THRESHOLD in particular is also a global in
   section-terminal-hub.js, which was throwing a SyntaxError on every page that
   carried both. */
(() => {
const DRAG_THRESHOLD = 6;
const SETTLE_MS = 120;

/**
 * <mb-loop-rail> — a product rail with no end.
 *
 * The track stays a real scroll container, so touch scrolling, momentum and
 * scroll snapping are the browser's, not ours. Looping is done by tiling:
 * enough cards are cloned onto both ends to cover a viewport, and whenever the
 * scroll position leaves the middle copy it is moved by exactly one set width.
 * The content repeats with that period, so the jump is invisible: the same
 * pixels stay under the same finger.
 *
 * Two rules keep that jump from being felt:
 *   - it is deferred until scrolling settles, because assigning scrollLeft
 *     during an iOS fling kills the fling;
 *   - it runs immediately anyway if the position is within one card of a real
 *     edge, because hitting the true end is worse than a clipped fling.
 *
 * Positions are handled as a distance from the start edge, `Math.abs(scrollLeft)`,
 * and written back through a calibrated sign, so RTL and LTR need no separate
 * code paths and no assumption about which scrollLeft convention the browser uses.
 */
class MbLoopRail extends HTMLElement {
  constructor() {
    super();
    this.clones = [];
    this.enabled = false;
    this.sign = 1;
  }

  connectedCallback() {
    this.track = this.querySelector('[data-grid-slider]');
    if (!this.track) return;

    this.items = [...this.track.children].filter((el) => !el.dataset.railClone);
    if (this.items.length < 2) return;

    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.onScrollBound = () => this.onScroll();
    this.onPointerMoveBound = (event) => this.onPointerMove(event);
    this.onPointerUpBound = (event) => this.onPointerUp(event);
    this.rebuildBound = () => this.build();

    this.build();

    this.track.addEventListener('scroll', this.onScrollBound, { passive: true, signal });
    this.track.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal });
    this.track.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
    this.addEventListener('click', (event) => this.onClick(event), { capture: true, signal });
    document.addEventListener('theme:resize:width', this.rebuildBound, { signal });
    document.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event), { signal });

    // The cards are measured to the pixel, so a late layout shift, a lazy image
    // landing or a webfont swapping in, would leave the whole tiling off by that
    // much per card. Watching the first card catches it and rebuilds, but only
    // when the card really changed size, so an ordinary scroll is never yanked.
    this.resizeObserver = new ResizeObserver(() => {
      const step = this.measureStep();
      if (!step || Math.abs(step - this.step) < 1) return;
      this.build();
    });
    this.resizeObserver.observe(this.items[0]);

    if (document.readyState !== 'complete') {
      window.addEventListener('load', () => this.normalize(), { once: true, signal });
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.dragController?.abort();
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.frame);
    clearTimeout(this.settleTimer);
  }

  /* ---------------------------------------------------------------- geometry */

  /** Distance scrolled from the start edge, positive in both directions. */
  pos() {
    return Math.abs(this.track.scrollLeft);
  }

  setPos(value) {
    this.track.scrollLeft = this.sign * value;
  }

  /**
   * One card plus its gap, measured between two live cards rather than read
   * from CSS, so it stays right whatever the breakpoint does to the widths.
   */
  measureStep() {
    const first = this.items[0].getBoundingClientRect();
    const second = this.items[1].getBoundingClientRect();
    return Math.abs(second.left - first.left);
  }

  /**
   * Physical distance from the track's start edge to the element's start edge,
   * which is the left edge in LTR and the right edge in RTL. Adding it to the
   * current position gives the position at which that card sits flush with the
   * start of the rail, whatever padding the track carries.
   */
  startDelta(el) {
    const item = el.getBoundingClientRect();
    const track = this.track.getBoundingClientRect();
    return this.sign > 0 ? item.left - track.left : track.right - item.right;
  }

  /* ------------------------------------------------------------------- build */

  build() {
    this.clones.forEach((clone) => clone.remove());
    this.clones = [];
    this.enabled = false;

    const step = this.measureStep();
    const viewport = this.track.clientWidth;
    if (!step || !viewport) return;

    // Nothing to loop through if the whole set is on screen already.
    const setWidth = this.items.length * step;
    if (setWidth <= viewport + 1) {
      this.track.classList.remove('is-loop-rail');
      return;
    }

    // Enough clones to cover a viewport plus one card of slack on each side.
    const cover = Math.min(this.items.length, Math.ceil(viewport / step) + 1);

    // Prepending in reverse keeps the tail clones in their original order.
    this.items.slice(-cover).reverse().forEach((item) => this.track.prepend(this.cloneCard(item)));
    this.items.slice(0, cover).forEach((item) => this.track.append(this.cloneCard(item)));

    this.step = step;
    this.setWidth = setWidth;
    this.enabled = true;
    this.track.classList.add('is-loop-rail');

    this.calibrateSign();

    // Open on the first real card, flush with the start of the rail. This is the
    // only place a position is computed from a stored number; everything after
    // it reads the live geometry, because a lazy image or a webfont landing
    // moves every card and would leave a remembered position wrong by a card.
    this.setPos(this.pos() + this.startDelta(this.items[0]));
    this.ensureControls();
  }

  /**
   * RTL scroll containers report scrollLeft as a negative offset in current
   * browsers, but that is worth proving rather than assuming: write a known
   * position with the assumed sign and keep it only if it reads back.
   */
  calibrateSign() {
    this.sign = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;

    const probe = Math.min(50, Math.max(0, this.track.scrollWidth - this.track.clientWidth));
    this.setPos(probe);
    if (Math.abs(this.pos() - probe) > 2) this.sign = -this.sign;
  }

  /**
   * Clones are decoration: they carry no identity and no keyboard or screen
   * reader presence, so every product is announced and tabbed to exactly once.
   * The accent colour is copied as an inline value because the card's stripe
   * comes from an :nth-child rule, which a clone in a new position would
   * otherwise resolve to a different colour than the card it repeats.
   */
  cloneCard(item) {
    const clone = item.cloneNode(true);
    clone.dataset.railClone = 'true';
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('id');
    clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((el) => {
      el.setAttribute('tabindex', '-1');
    });

    const accent = getComputedStyle(item).getPropertyValue('--airport-card-accent');
    if (accent) clone.style.setProperty('--airport-card-accent', accent.trim());

    this.clones.push(clone);
    return clone;
  }

  /* ------------------------------------------------------------------ looping */

  onScroll() {
    if (!this.enabled) return;

    // Browsers coalesce scroll events to one per frame, so this reads layout
    // once per frame without a rAF of its own. An earlier version parked the
    // work in requestAnimationFrame behind a guard flag, which wedged shut for
    // good anywhere the callback was throttled.
    const pos = this.pos();
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    if (pos < this.step || pos > maxScroll - this.step) this.normalize();

    this.updateThumb();

    clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => this.normalize(), SETTLE_MS);
  }

  /**
   * Bring the position back into the middle copy. The shift is exactly one set
   * width, which is also a whole number of snap points, so the browser has no
   * reason to re-snap and nothing moves on screen.
   */
  normalize() {
    if (!this.enabled || this.isPointerDown) return;

    // How far the first real card sits from the start edge: zero or negative
    // while the middle copy is on screen, positive once the head clones are.
    const delta = this.startDelta(this.items[0]);
    if (delta > 1) {
      this.setPos(this.pos() + this.setWidth);
    } else if (delta <= -this.setWidth) {
      this.setPos(this.pos() - this.setWidth);
    }

    this.updateThumb();
  }

  /* ---------------------------------------------------------------- controls */

  /**
   * A control strip under the rail: previous, a progress line, next. It sits
   * below rather than floating over the cards, because a rail that loops has no
   * scrollbar, no end and nothing else that says it can be moved, and arrows
   * hidden over the artwork until hover say it only to people who already knew.
   * Desktop only, by CSS: on a phone the rail is swiped.
   */
  ensureControls() {
    if (!this.controls) {
      if (!window.theme?.sliderArrows) return;

      this.controls = document.createElement('div');
      this.controls.className = 'mb-rail__controls';
      // A button at each end with the line between them, and real space either
      // side of the line so it never reads as running into a button.
      this.controls.innerHTML =
        window.theme.sliderArrows.prev +
        '<span class="mb-rail__line"><span class="mb-rail__thumb"></span></span>' +
        window.theme.sliderArrows.next;
      this.append(this.controls);

      this.controls.querySelectorAll('[data-button-arrow]').forEach((button) => {
        // Nothing here ever reaches an end, so an arrow must never disable
        // itself: that is the one signal this rail is not supposed to give.
        button.disabled = false;
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this.page(button.hasAttribute('data-button-next') ? 1 : -1);
        });
      });

      this.thumb = this.controls.querySelector('.mb-rail__thumb');
    }

    this.updateThumb();
  }

  /**
   * The line reports the position inside ONE set of products, not inside the
   * whole tiled track, so it moves smoothly and wraps with the rail instead of
   * jumping when the tiling shifts, and it never states how many products there
   * are. It is a position, not a count.
   */
  updateThumb() {
    if (!this.thumb || !this.setWidth) return;

    const size = Math.min(1, this.track.clientWidth / this.setWidth);
    let progress = (-this.startDelta(this.items[0]) % this.setWidth) / this.setWidth;
    if (progress < 0) progress += 1;

    this.thumb.style.setProperty('--rail-thumb-size', `${(size * 100).toFixed(2)}%`);
    this.thumb.style.setProperty('--rail-thumb-pos', `${(progress * (1 - size) * 100).toFixed(2)}%`);
  }

  /** One viewport of cards, rounded down to whole cards so it lands on a snap point. */
  page(direction) {
    if (!this.step) return;

    const perView = Math.max(1, Math.floor(this.track.clientWidth / this.step));
    this.track.scrollBy({
      left: this.sign * direction * perView * this.step,
      behavior: this.prefersReducedMotion.matches ? 'instant' : 'smooth',
    });
  }

  /* -------------------------------------------------------------------- drag */

  onPointerDown(event) {
    if (event.pointerType === 'touch' || event.button !== 0) return;

    this.isPointerDown = true;
    this.isDragging = false;
    this.dragPointerId = event.pointerId;
    this.dragLastX = event.clientX;
    this.dragDistance = 0;

    this.dragController?.abort();
    this.dragController = new AbortController();
    const options = { signal: this.dragController.signal };
    window.addEventListener('pointermove', this.onPointerMoveBound, options);
    window.addEventListener('pointerup', this.onPointerUpBound, options);
    window.addEventListener('pointercancel', this.onPointerUpBound, options);
  }

  onPointerMove(event) {
    if (!this.isPointerDown || event.pointerId !== this.dragPointerId) return;

    const movement = event.clientX - this.dragLastX;
    this.dragLastX = event.clientX;
    this.dragDistance += Math.abs(movement);

    if (!this.isDragging) {
      if (this.dragDistance <= DRAG_THRESHOLD) return;
      this.isDragging = true;
      this.track.classList.add('is-dragging');
    }

    // Physical axis, so this needs no direction handling.
    this.track.scrollLeft -= movement;
  }

  onPointerUp(event) {
    if (!this.isPointerDown || event.pointerId !== this.dragPointerId) return;

    this.isPointerDown = false;
    this.dragPointerId = null;
    this.dragController?.abort();
    this.dragController = null;

    if (!this.isDragging) return;

    this.isDragging = false;
    this.track.classList.remove('is-dragging');
    this.suppressClick = true;
    clearTimeout(this.suppressClickTimer);
    this.suppressClickTimer = setTimeout(() => {
      this.suppressClick = false;
    }, 0);

    this.normalize();
  }

  /** A drag that ends on a card must not also open it. */
  onClick(event) {
    if (!this.suppressClick) return;
    event.preventDefault();
    event.stopPropagation();
  }

  /** Theme editor: bring the selected block into view inside the rail. */
  onBlockSelect(event) {
    const card = event.target?.closest?.('[data-grid-item]');
    if (!card || !this.contains(card)) return;

    const delta = card.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
    this.track.scrollBy({ left: delta, behavior: 'smooth' });
  }
}

if (!customElements.get('mb-loop-rail')) {
  customElements.define('mb-loop-rail', MbLoopRail);
}
})();
